/* ===========================================================================
   pdp-photoswipe.js — full-screen PDP image zoom (PhotoSwipe v5).

   Replaces the old in-place cursor-zoom. Clicking the main gallery image (or the
   "Click to zoom" hint) opens a full-screen viewer with pinch / wheel / double-tap
   zoom, drag-pan, prev/next, a custom Zoom In / Out / Reset control set, and a
   bottom thumbnail strip synced to the active slide — matching the rugs.com bar.

   Config is handed in via window.ArteverkPswp (set inline in main-product.liquid)
   so this stays a static, Liquid-free asset (same pattern as spec-sheet.js):
     { coreUrl, lightboxUrl, images: [{ src, width, height, thumb, alt }, ...] }

   Defensive: every step is guarded; if PhotoSwipe fails to load the PDP is
   unaffected (the click simply does nothing).
   =========================================================================== */
(async function () {
  "use strict";
  var cfg = window.ArteverkPswp;
  if (!cfg || !Array.isArray(cfg.images) || cfg.images.length === 0) return;

  var gallery = document.querySelector("[data-gallery]");
  var stage = gallery && gallery.querySelector("[data-zoom]");
  if (!gallery || !stage) return;

  // Dynamically import the vendored ESM modules from their Shopify asset URLs.
  var PhotoSwipeLightbox, PhotoSwipe;
  try {
    PhotoSwipeLightbox = (await import(cfg.lightboxUrl)).default;
    PhotoSwipe = (await import(cfg.coreUrl)).default;
  } catch (e) {
    return; // library unavailable — leave the PDP untouched
  }

  // The hint is hidden until JS confirms zoom is wired (works on touch too now).
  gallery.classList.add("zoom-ready");

  var lightbox = new PhotoSwipeLightbox({
    dataSource: cfg.images,
    pswpModule: PhotoSwipe,
    bgOpacity: 0.96,
    initialZoomLevel: "fit",
    secondaryZoomLevel: 1.6, // gentle double-tap zoom (was a hard jump to 2.5)
    maxZoomLevel: 4,
    wheelToZoom: true,
    zoom: false,           // drop the built-in magnifier (it jumped to secondary);
                           // our custom In/Out/Reset buttons step gradually instead
    counter: true,
    arrowKeys: true,
    returnFocus: true,
    padding: { top: 64, bottom: 96, left: 16, right: 16 }, // room for toolbar + thumb strip
  });

  // ---- helpers: zoom controls ----------------------------------------------
  function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }
  function center(pswp) { return { x: pswp.viewportSize.x / 2, y: pswp.viewportSize.y / 2 }; }
  function zoomTo(level) {
    var pswp = lightbox.pswp; if (!pswp) return;
    var s = pswp.currSlide; if (!s) return;
    var lvl = clamp(level, s.zoomLevels.min, s.zoomLevels.max);
    pswp.zoomTo(lvl, center(pswp), 333);
  }
  function zoomBy(factor) {
    var s = lightbox.pswp && lightbox.pswp.currSlide; if (!s) return;
    zoomTo(s.currZoomLevel * factor);
  }
  function zoomReset() {
    var s = lightbox.pswp && lightbox.pswp.currSlide; if (!s) return;
    zoomTo(s.zoomLevels.initial);
  }

  var ICON = {
    in:    '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M10 4v12M4 10h12" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linecap="round" transform="translate(1 1)"/><circle cx="10" cy="10" r="7.2" stroke="currentColor" stroke-width="1.6" fill="none"/><path d="M15.5 15.5l4 4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>',
    out:   '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M5 11h12" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linecap="round" transform="translate(0 -1)"/><circle cx="10" cy="10" r="7.2" stroke="currentColor" stroke-width="1.6" fill="none"/><path d="M15.5 15.5l4 4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>',
    reset: '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M4 9a8 8 0 1 1-.6 5" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linecap="round"/><path d="M3.5 4v5h5" stroke="currentColor" stroke-width="1.6" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  };

  // ---- custom UI: zoom buttons + bottom thumbnail strip ---------------------
  lightbox.on("uiRegister", function () {
    var pswp = lightbox.pswp;

    pswp.ui.registerElement({ name: "av-zoom-in", order: 8, isButton: true, html: ICON.in,
      title: "Zoom in", ariaLabel: "Zoom in", onClick: function () { zoomBy(1.5); } });
    pswp.ui.registerElement({ name: "av-zoom-out", order: 9, isButton: true, html: ICON.out,
      title: "Zoom out", ariaLabel: "Zoom out", onClick: function () { zoomBy(1 / 1.5); } });
    pswp.ui.registerElement({ name: "av-zoom-reset", order: 10, isButton: true, html: ICON.reset,
      title: "Reset zoom", ariaLabel: "Reset zoom", onClick: function () { zoomReset(); } });

    // Bottom thumbnail strip (only when there's more than one image).
    if (cfg.images.length > 1) {
      pswp.ui.registerElement({
        name: "av-thumbs",
        appendTo: "root",
        onInit: function (el) {
          el.className = "pswp__av-thumbs";
          var btns = cfg.images.map(function (img, i) {
            var b = document.createElement("button");
            b.type = "button";
            b.className = "pswp__av-thumb";
            b.setAttribute("aria-label", "View " + (i + 1));
            var im = document.createElement("img");
            im.src = img.thumb || img.src;
            im.alt = "";
            im.loading = "lazy";
            b.appendChild(im);
            b.addEventListener("click", function () { pswp.goTo(i); });
            el.appendChild(b);
            return b;
          });
          function sync() {
            for (var i = 0; i < btns.length; i++) {
              btns[i].classList.toggle("is-active", i === pswp.currIndex);
            }
            var active = btns[pswp.currIndex];
            if (active && active.scrollIntoView) {
              active.scrollIntoView({ block: "nearest", inline: "center" });
            }
          }
          pswp.on("change", sync);
          sync();
        },
      });
    }
  });

  // Reset zoom whenever the slide changes (so each rug opens fit-to-screen).
  lightbox.on("change", function () {
    var s = lightbox.pswp && lightbox.pswp.currSlide;
    if (s && s.currZoomLevel !== s.zoomLevels.initial) { /* PhotoSwipe handles per-slide init */ }
  });

  lightbox.init();

  // ---- open from the gallery at the currently-shown image -------------------
  function shownIndex() {
    var imgs = stage.querySelectorAll(".stage-img");
    for (var i = 0; i < imgs.length; i++) {
      if (imgs[i].classList.contains("shown")) return i;
    }
    return 0;
  }
  function open(e) {
    if (e) e.preventDefault();
    lightbox.loadAndOpen(shownIndex());
  }
  stage.addEventListener("click", open);
  // Keyboard affordance: Enter/Space on the focused stage opens the viewer.
  stage.setAttribute("tabindex", "0");
  stage.setAttribute("role", "button");
  stage.setAttribute("aria-label", "Open full-screen image viewer");
  stage.addEventListener("keydown", function (e) {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(); }
  });
})();
